import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { Feature, FeatureCollection } from 'geojson'
import * as h3 from 'h3-js'
import { firstValueFrom } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { MapService } from 'src/app/core/services/map.service'
import {
  isAreaSimWarningDataModel,
  isSegmentSimWarningDataModel,
  SimWarningDataModel,
} from 'src/app/shared/models/sim-warning-creation.model'

export interface LegacyImportFailure {
  id: string
  type: 'segment' | 'area' | 'unknown'
}

export interface LegacyImportResult {
  featureCollection: FeatureCollection<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>
  failedWarnings: LegacyImportFailure[]
}

export interface RoadSegmentGeometryRequest {
  segmentId: number
  latitude: number
  longitude: number
}

@Injectable()
export class SimulationWarningsLegacyImportService {
  constructor(
    private readonly mapService: MapService,
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}
  // TODO: delete this legacy compatibility service in the future when all imports use the new format.
  // Remove this service and the UI entry point once the deprecated legacy payload format is no longer required.
  public readonly legacyImportCompatibilityEnabled = true

  // TODO: delete this method in the future when legacy payload import is removed.
  async importLegacyWarnings(raw: unknown): Promise<LegacyImportResult> {
    if (!this.legacyImportCompatibilityEnabled) {
      return {
        featureCollection: {
          type: 'FeatureCollection',
          features: [],
        },
        failedWarnings: [],
      }
    }

    return this.normalizeLegacyWarnings(raw)
  }

  // TODO: delete this helper in the future once the legacy import format is removed.
  private async normalizeLegacyWarnings(raw: unknown): Promise<LegacyImportResult> {
    const entries = Array.isArray(raw) ? raw : [raw]
    const features: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>[] = []
    const failedWarnings: LegacyImportFailure[] = []

    for (const entry of entries) {
      if (!entry || typeof entry !== 'object') {
        continue
      }

      const feature = await this.createFeatureFromLegacyWarning(entry)
      if (!feature) {
        const id = this.getLegacyWarningId(entry)
        const type = this.getLegacyWarningType(entry)
        failedWarnings.push({ id, type })
        continue
      }

      features.push(feature)
    }

    return {
      featureCollection: {
        type: 'FeatureCollection',
        features,
      },
      failedWarnings,
    }
  }

  // TODO: delete this legacy conversion helper in the future after the old payload format is retired.
  private async createFeatureFromLegacyWarning(
    warning: Record<string, unknown>,
  ): Promise<Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel> | null> {
    const source = { ...(warning ?? {}) }
    const featureId =
      typeof source['featureId'] === 'string' ? source['featureId'] : crypto.randomUUID()
    const ttlValue =
      typeof source['ttl'] === 'string' || typeof source['ttl'] === 'number'
        ? Number(source['ttl']) || 0
        : 0
    const targetsValue =
      source['targets'] === null || source['targets'] === undefined
        ? null
        : Array.isArray(source['targets'])
          ? source['targets'].map((target) => String(target))
          : [String(source['targets'])]

    const properties = {
      ...source,
      featureId,
      ttl: ttlValue,
      targets: targetsValue,
      confidence: typeof source['confidence'] === 'number' ? source['confidence'] : 1.0,
      dfrsClassification:
        (source['dfrsClassification'] as SimWarningDataModel['dfrsClassification']) ?? 'DFRS_NONE',
      valid: typeof source['valid'] === 'boolean' ? source['valid'] : true,
    } as SimWarningDataModel

    const geometry = await this.buildLegacyGeometry(properties)
    if (!geometry) {
      return null
    }

    return {
      type: 'Feature',
      geometry,
      properties,
    }
  }

  // TODO: delete this geometry fallback in the future once legacy warning payloads no longer need conversion.
  private async buildLegacyGeometry(
    warning: SimWarningDataModel,
  ): Promise<GeoJSON.LineString | GeoJSON.Polygon | null> {
    if (isAreaSimWarningDataModel(warning) && warning.h3id) {
      if (!h3.isValidCell(warning.h3id)) {
        return null
      }

      try {
        const boundary = h3.cellToBoundary(warning.h3id, true)
        return {
          type: 'Polygon',
          coordinates: [boundary.map(([lat, lng]) => [lat, lng])],
        }
      } catch {
        return null
      }
    }

    if (isSegmentSimWarningDataModel(warning) && warning.segmentId) {
      return this.resolveSegmentGeometry(warning.segmentId, warning.latitude, warning.longitude)
    }

    return null
  }

  private async resolveSegmentGeometry(
    segmentId: string,
    latitude: number,
    longitude: number,
  ): Promise<GeoJSON.LineString | null> {
    const url = `${this.config.backend_url}rcs/segments/geometries`
    const payload: RoadSegmentGeometryRequest[] = [
      {
        segmentId: Number(segmentId),
        latitude: latitude,
        longitude: longitude,
      },
    ]

    try {
      const response = await firstValueFrom(this.http.post<Record<string, any>>(url, payload))

      const geometry = this.extractGeometryFromBackendResponse(response, Number(segmentId))
      return geometry
    } catch {
      return null
    }
  }

  private extractGeometryFromBackendResponse(
    geometryResponse: Record<string, any>,
    segmentId: number,
  ): GeoJSON.LineString | null {
    const segmentIdKey = String(segmentId)
    const matchedGeometry = JSON.parse(
      geometryResponse[segmentIdKey] ?? geometryResponse[segmentId],
    )
    if (
      !matchedGeometry ||
      typeof matchedGeometry !== 'object' ||
      !matchedGeometry.coordinates ||
      matchedGeometry.type !== 'LineString'
    ) {
      return null
    }

    return matchedGeometry as GeoJSON.LineString
  }

  private getLegacyWarningType(warning: Record<string, unknown>): LegacyImportFailure['type'] {
    if (typeof warning['segmentId'] === 'string' && warning['segmentId']) {
      return 'segment'
    }

    if (typeof warning['h3id'] === 'string' && warning['h3id']) {
      return 'area'
    }

    return 'unknown'
  }

  private getLegacyWarningId(warning: Record<string, unknown>): string {
    if (typeof warning['segmentId'] === 'string' && warning['segmentId']) {
      return warning['segmentId']
    }

    if (typeof warning['h3id'] === 'string' && warning['h3id']) {
      return warning['h3id']
    }

    return 'unknown'
  }
}
